import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import SuperTableV2 from "../SuperTableV2";
import { toPlainText } from "./ArtifactTable";
import { AprendizajesServices } from "./BriefcaseServices";
import { fetchAprendizajes } from "../../redux";
import planningImg from "../../assets/planning-1.png";

registerLocale("es", es);

// Excluye sábados y domingos del datepicker. Duplicado del helper homónimo de ArtifactTable.jsx (no
// exportado allí) porque ese archivo es intocable por decisión del spec 004 §6.1 (componente compartido
// por Riesgos/Compromisos/Novedades). Es una función pura de 3 líneas: duplicarla es más barato que
// reabrir esa decisión.
const isWeekday = (date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

// Color del badge de Tipo, resuelto en el front a partir del ID literal y estable del catálogo
// (CRITICO/MEDIA/NO_CRITICO). El VALOR (etiqueta) sale del DDL vía SP_DDL_PORTAFOLIO_APRENDIZAJES.
// Mismo patrón visual que los badges de estado ya usados en Boards.jsx/Projects.jsx: span plano
// "badge bg-{color} text-white" (fondo sólido de marca vía $theme-colors + texto SIEMPRE blanco,
// incluso sobre warning) en vez del componente Badge de react-bootstrap (que da texto oscuro
// automático en warning y no es lo que se ve en el resto de la app).
const TIPO_BADGE_COLOR = {
  CRITICO: "danger",
  MEDIA: "warning",
  NO_CRITICO: "gray",
};

// "gray" no es una variante de $theme-colors (bg-secondary en este proyecto es rosa/rojizo, no gris):
// se resuelve con el gris `muted` del theme de marca (color-theme.js) vía estilo inline en vez de clase.
const GRAY_BADGE_STYLE = { backgroundColor: "#6c757d" };

const toDateInputValue = (val) => (val ? String(val).slice(0, 10) : "");

// Matriz bespoke "Aprendizajes" (spec 004, KR2 - Lecciones Aprendidas). NO delega en ArtifactTable.jsx
// (decisión cerrada §6.1): tiene un flujo propio de reutilización en el alta (modal de 2 pasos con
// herencia + bloqueo de campos, RF-5) y un ciclo de vida propio (borrado FÍSICO con validación de
// integridad referencial, RF-8) que el genérico compartido por Riesgos/Compromisos/Novedades no soporta.
// Reutiliza la maquinaria transversal: SuperTableV2, toPlainText (anti-XSS, H-05), isWeekday + DatePicker,
// swal, sub-slice Redux (fetchAprendizajes) y la prop `proyectos` ya cargada por EditBriefcase (sin
// endpoint nuevo para "Sprint/Ciclo").
const AprendizajesBriefcase = ({ idPortafolio, proyectos = [] }) => {
  const [t] = useTranslation("briefcase");
  const dispatch = useDispatch();
  const slice = useSelector((state) => state.briefcaseArtifacts.aprendizajes);

  const [ddl, setDdl] = useState([]);
  const [reutilizables, setReutilizables] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Paso previo del alta (RF-5): "si" | "no". Solo relevante mientras se crea (no se muestra editando).
  const [esReutilizado, setEsReutilizado] = useState("no");
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (idPortafolio) {
      dispatch(fetchAprendizajes(idPortafolio));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPortafolio]);

  useEffect(() => {
    let cancelled = false;
    if (idPortafolio) {
      AprendizajesServices.getAprendizajesDdl(idPortafolio)
        .then((data) => {
          if (!cancelled) setDdl(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (!cancelled) setDdl([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [idPortafolio]);

  useEffect(() => {
    let cancelled = false;
    if (idPortafolio) {
      AprendizajesServices.getAprendizajesReutilizacion(idPortafolio)
        .then((data) => {
          if (!cancelled) setReutilizables(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (!cancelled) setReutilizables([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [idPortafolio]);

  // Opciones dinámicas para los selects ({ value, label } con etiqueta literal). El de proyectos se puebla
  // con la prop `proyectos` (homólogo de IdProyecto en Impedimentos, sin endpoint nuevo).
  const dynamicOptions = useMemo(
    () => ({
      proyectos: (proyectos || []).map((p) => ({ value: p.IdProyecto, label: p.Nombre })),
      tipo: ddl
        .filter((r) => r.DDL === "TIPO_APRENDIZAJE")
        .map((r) => ({ value: r.ID, label: r.VALOR })),
    }),
    [ddl, proyectos]
  );

  const tipoLabel = (idTipo) => {
    const opt = (dynamicOptions.tipo || []).find((o) => String(o.value) === String(idTipo));
    return opt ? opt.label : idTipo || "";
  };

  // RF-7: en edición, Tipo/Título/Descripción siempre bloqueados. RF-5: en alta, se bloquean cuando el
  // usuario elige "reutilizado" (heredan del origen).
  const camposHeredadosBloqueados = !!editingId || esReutilizado === "si";

  const items = slice.items || [];

  const resetForm = () => ({
    IdProyecto: "",
    Fecha: "",
    IdTipo: "",
    Titulo: "",
    Descripcion: "",
    IdAprendizajeOrigen: "",
  });

  const openCreate = () => {
    setEditingId(null);
    setEsReutilizado("no");
    setFormData(resetForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.IdAprendizaje);
    setEsReutilizado(row.Reutilizado ? "si" : "no");
    setFormData({
      IdProyecto: row.IdProyecto || "",
      Fecha: toDateInputValue(row.Fecha),
      IdTipo: row.IdTipo || "",
      Titulo: row.Titulo || "",
      Descripcion: row.Descripcion || "",
      IdAprendizajeOrigen: row.IdAprendizajeOrigen || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setEsReutilizado("no");
    setFormData({});
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Cambiar de paso (Sí/No) limpia siempre los 3 campos heredables: evita arrastrar valores de un modo al
  // otro (ej. texto tecleado en "No" quedando "fantasma" tras pasar a "Sí").
  const handleEsReutilizadoChange = (value) => {
    setEsReutilizado(value);
    setFormData((prev) => ({
      ...prev,
      IdAprendizajeOrigen: "",
      IdTipo: "",
      Titulo: "",
      Descripcion: "",
    }));
  };

  // Al elegir el aprendizaje origen, hereda Tipo/Título/Descripción (RF-5) desde los items ya cargados por
  // el LISTAR (sin endpoint nuevo, FA-5).
  const handleSelectOrigen = (idOrigen) => {
    const origen = reutilizables.find((it) => String(it.IdAprendizaje) === String(idOrigen));
    if (!origen) {
      setFormData((prev) => ({ ...prev, IdAprendizajeOrigen: "", IdTipo: "", Titulo: "", Descripcion: "" }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      IdAprendizajeOrigen: idOrigen,
      IdTipo: origen.IdTipo || "",
      Titulo: origen.Titulo || "",
      Descripcion: origen.Descripcion || "",
    }));
  };

  const validate = () => {
    if (!formData.IdProyecto || !formData.Fecha) return "required";
    if (!editingId) {
      if (esReutilizado === "si" && !formData.IdAprendizajeOrigen) return "required";
      if (
        !formData.IdTipo ||
        !String(formData.Titulo || "").trim() ||
        !String(formData.Descripcion || "").trim()
      ) {
        return "required";
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (validate()) {
      swal(t("artefactos_error_title"), t("artefactos_validation_required"), "warning");
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        // RF-7: solo Fecha + Sprint/Ciclo viajan al backend; el SP tampoco declara los otros parámetros.
        await AprendizajesServices.update(idPortafolio, editingId, {
          IdProyecto: formData.IdProyecto,
          Fecha: formData.Fecha,
        });
      } else {
        await AprendizajesServices.create(idPortafolio, {
          IdProyecto: formData.IdProyecto,
          Fecha: formData.Fecha,
          IdTipo: formData.IdTipo,
          Titulo: formData.Titulo,
          Descripcion: formData.Descripcion,
          IdAprendizajeOrigen: esReutilizado === "si" ? formData.IdAprendizajeOrigen || null : null,
        });
      }
      swal(
        t("artefactos_done_title"),
        editingId ? t("artefactos_done_update") : t("artefactos_done_create"),
        "success"
      );
      closeModal();
      dispatch(fetchAprendizajes(idPortafolio));
    } catch (e) {
      swal(t("artefactos_error_title"), t("artefactos_error_save"), "error");
    } finally {
      setSaving(false);
    }
  };

  // RF-8: si el backend responde 409 (HAS_REUSES) es porque el aprendizaje tiene hijos que lo reutilizan;
  // se muestra el mensaje de integridad en vez del error genérico de borrado.
  const handleDelete = (row) => {
    swal({
      title: t("artefactos_swal_delete_title"),
      text: t("artefactos_swal_delete_text"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      try {
        await AprendizajesServices.remove(idPortafolio, row.IdAprendizaje);
        swal(t("artefactos_done_title"), t("artefactos_done_delete"), "success");
        dispatch(fetchAprendizajes(idPortafolio));
      } catch (e) {
        const status = e && e.response && e.response.status;
        if (status === 409) {
          swal(t("artefactos_error_title"), t("aprendizajes_error_has_reuses"), "error");
        } else {
          swal(t("artefactos_error_title"), t("artefactos_error_delete"), "error");
        }
      }
    });
  };

  // Columna de acciones + las 6 columnas de datos de RF-3 (sin columna ID; texto libre siempre plano).
  const tableColumns = [
    {
      caption: t("artefactos_actions"),
      selector: "__actions",
      width: 110,
      align: "center",
      customRender: (rowWrap) => {
        const row = rowWrap.data;
        return (
          <div className="d-flex justify-content-center">
            <Button
              variant="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={() => openEdit(row)}
              title={t("artefactos_btn_edit")}
            >
              <i className="fa fa-pen" />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDelete(row)}
              title={t("artefactos_btn_delete")}
            >
              <i className="fa fa-trash" />
            </Button>
          </div>
        );
      },
    },
    {
      caption: t("aprendizajes_col_fecha"),
      selector: "Fecha",
      width: 120,
      align: "center",
      customRender: (rowWrap) => <span>{toDateInputValue(rowWrap.data.Fecha)}</span>,
    },
    {
      caption: t("aprendizajes_col_sprint"),
      selector: "NombreProyecto",
      width: 190,
      align: "center",
      customRender: (rowWrap) => (
        <span title={toPlainText(rowWrap.data.NombreProyecto)}>
          {toPlainText(rowWrap.data.NombreProyecto)}
        </span>
      ),
    },
    {
      caption: t("aprendizajes_col_tipo"),
      selector: "IdTipo",
      width: 170,
      align: "center",
      customRender: (rowWrap) => {
        const idTipo = rowWrap.data.IdTipo;
        const color = TIPO_BADGE_COLOR[idTipo] || "gray";
        return color === "gray" ? (
          <span className="badge text-white" style={GRAY_BADGE_STYLE}>
            {tipoLabel(idTipo)}
          </span>
        ) : (
          <span className={`badge bg-${color} text-white`}>{tipoLabel(idTipo)}</span>
        );
      },
    },
    {
      caption: t("aprendizajes_col_titulo"),
      selector: "Titulo",
      width: 250,
      align: "center",
      customRender: (rowWrap) => (
        <span title={toPlainText(rowWrap.data.Titulo)}>{toPlainText(rowWrap.data.Titulo)}</span>
      ),
    },
    {
      caption: t("aprendizajes_col_descripcion"),
      selector: "Descripcion",
      width: 300,
      align: "center",
      customRender: (rowWrap) => {
        const txt = toPlainText(rowWrap.data.Descripcion);
        const short = txt.length > 90 ? `${txt.slice(0, 90)}…` : txt;
        return <span title={txt}>{short}</span>;
      },
    },
    {
      caption: t("aprendizajes_col_reutilizado"),
      selector: "Reutilizado",
      width: 130,
      align: "center",
      customRender: (rowWrap) => {
        const si = rowWrap.data.Reutilizado === true || rowWrap.data.Reutilizado === 1;
        if (!si) {
          return (
            <span className="badge text-white" style={GRAY_BADGE_STYLE}>
              {t("aprendizajes_reutilizado_no")}
            </span>
          );
        }
        return (
          <span className="badge bg-info text-white">
            <i className="fa fa-recycle mr-1" />
            {t("aprendizajes_reutilizado_si")}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      {slice.loading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" variant="danger" />
        </div>
      ) : slice.error ? (
        <p className="text-center text-muted py-4">{t("artefactos_error_load")}</p>
      ) : items.length > 0 ? (
        <SuperTableV2 data={items} columns={tableColumns} title_searchbox={t("artefactos_searchbox")} />
      ) : (
        // RF-11: estado vacío ilustrado con botón de alta.
        <div className="text-center pt-4 mt-4">
          <img
            src={planningImg}
            alt=""
            className="img-fluid mb-3 animated pulse slower infinite"
            width="200"
          />
          <h5>{t("aprendizajes_empty_title")}</h5>
          <p>{t("aprendizajes_empty_desc")}</p>
          <Button onClick={openCreate}>
            <i className="fa fa-plus-circle mr-2"></i>
            {t("aprendizajes_empty_btn")}
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <Button
          variant="success"
          className="position-fixed shadow animate__animated animate__fadeInUp"
          style={{ bottom: "1rem", right: "1rem" }}
          onClick={openCreate}
        >
          <i className="fa fa-plus-circle mr-2" />
          {t("aprendizajes_empty_btn")}
        </Button>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="lg" contentClassName="border-0 shadow-lg">
        <Modal.Header>
          <Modal.Title className="h5">
            {editingId ? t("artefactos_modal_edit_title") : t("artefactos_modal_create_title")}
          </Modal.Title>
          <Button variant="light" onClick={closeModal} aria-label="Close">
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {!editingId && (
              <div className="mb-3" style={{ backgroundColor: "#f3f3f3", borderRadius: "5px" }}>
                <div className="px-3 py-2 d-flex align-items-center">
                  <i className="fa fa-recycle mr-2 text-primary" />
                  <h6 className="text-primary mb-0">{t("aprendizajes_reuse_question")}</h6>
                </div>
                <div className="px-3 pt-2 pb-3">
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <Button
                      type="button"
                      variant={esReutilizado === "si" ? "success" : "outline-secondary"}
                      size="sm"
                      style={{ minWidth: "56px" }}
                      onClick={() => handleEsReutilizadoChange("si")}
                    >
                      {t("artefactos_yesno_si")}
                    </Button>
                    <Button
                      type="button"
                      variant={esReutilizado === "no" ? "danger" : "outline-secondary"}
                      size="sm"
                      style={{ minWidth: "56px" }}
                      onClick={() => handleEsReutilizadoChange("no")}
                    >
                      {t("artefactos_yesno_no")}
                    </Button>
                  </div>
                  {esReutilizado === "si" && (
                    <Form.Group className="mt-3 mb-0">
                      <Form.Label>
                        {t("aprendizajes_reuse_select_label")}
                        <span className="text-primary"> *</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={formData.IdAprendizajeOrigen || ""}
                        onChange={(e) => handleSelectOrigen(e.target.value)}
                      >
                        <option value="">{t("aprendizajes_reuse_select_placeholder")}</option>
                        {reutilizables.map((it) => (
                          <option key={it.IdAprendizaje} value={it.IdAprendizaje}>
                            {it.Titulo}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  )}
                </div>
              </div>
            )}

            <Row>
              <Col md={6} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("aprendizajes_label_fecha")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <DatePicker
                  selected={formData.Fecha ? new Date(`${formData.Fecha}T00:00:00`) : null}
                  onChange={(date) => {
                    if (!date) {
                      handleChange("Fecha", "");
                    } else {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, "0");
                      const d = String(date.getDate()).padStart(2, "0");
                      handleChange("Fecha", `${y}-${m}-${d}`);
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  autoComplete="off"
                  isClearable
                  popperProps={{ strategy: "fixed" }}
                  locale="es"
                  filterDate={isWeekday}
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={80}
                />
              </Col>
              <Col md={6} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("aprendizajes_label_sprint")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={formData.IdProyecto || ""}
                  onChange={(e) => handleChange("IdProyecto", e.target.value)}
                >
                  <option value="">{t("aprendizajes_sprint_select")}</option>
                  {(dynamicOptions.proyectos || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Control>
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("aprendizajes_label_tipo")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  disabled={camposHeredadosBloqueados}
                  value={formData.IdTipo || ""}
                  onChange={(e) => handleChange("IdTipo", e.target.value)}
                >
                  <option value="">{t("aprendizajes_tipo_select")}</option>
                  {(dynamicOptions.tipo || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={6} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("aprendizajes_label_titulo")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  disabled={camposHeredadosBloqueados}
                  value={formData.Titulo || ""}
                  onChange={(e) => handleChange("Titulo", e.target.value)}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("aprendizajes_label_descripcion")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  disabled={camposHeredadosBloqueados}
                  value={formData.Descripcion || ""}
                  onChange={(e) => handleChange("Descripcion", e.target.value)}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" onClick={closeModal} disabled={saving}>
            <i className="fa fa-times mr-2" />
            {t("artefactos_btn_cancel")}
          </Button>
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <i className="fa fa-save mr-2" />
                {t("artefactos_btn_save")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AprendizajesBriefcase;
